// @flow

import React, { PureComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import type { RouterHistory } from 'react-router';
import { i18n, getCompanyCurrency } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import DeleteGLAccountMutation from 'main-app/mutations/DeleteGLAccount';
import Table from 'shared/components/common/Table';

const Wrapper = styled.div`
  padding: 0 24px;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ type }) => {
    switch (type) {
      case 'ASSET': return '#E3F2FD';
      case 'LIABILITY': return '#FCE4EC';
      case 'EQUITY': return '#E8F5E9';
      case 'REVENUE': return '#E8F5E9';
      case 'COGS': return '#FFF3E0';
      case 'EXPENSE': return '#FFF3E0';
      default: return '#F5F5F5';
    }
  }};
  color: ${({ type }) => {
    switch (type) {
      case 'ASSET': return '#1565C0';
      case 'LIABILITY': return '#C62828';
      case 'EQUITY': return '#2E7D32';
      case 'REVENUE': return '#2E7D32';
      case 'COGS': return '#E65100';
      case 'EXPENSE': return '#E65100';
      default: return '#616161';
    }
  }};
`;

const BalanceCell = styled.span`
  font-weight: 600;
  color: ${({ value }) => (value < 0 ? '#C62828' : '#2E7D32')};
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#4CAF50' : '#BDBDBD')};
  margin-right: 6px;
`;

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: getCompanyCurrency(),
  }).format(value);
};

type Props = {
  history: RouterHistory,
  glAccountEdges: Array<Object>,
  onEdit: (account: Object) => void,
};

class ChartOfAccountsTable extends PureComponent<Props> {
  handleDelete = (account: Object) => {
    if (account.isSystem) {
      Actions.alertNotification(i18n.t('System accounts cannot be deleted.'), 'error');
      return;
    }
    DeleteGLAccountMutation.commit({
      variables: { input: { id: account.id } },
      onCompleted: () => {
        Actions.alertNotification(i18n.t('Account deleted successfully.'), 'success');
      },
      onError: () => {
        Actions.alertNotification(i18n.t('Error deleting account.'), 'error');
      },
    });
  };

  getColumns = () => {
    return [
      {
        Header: i18n.t('Account #'),
        accessor: 'node.accountNumber',
        width: 120,
      },
      {
        Header: i18n.t('Name'),
        accessor: 'node.name',
        minWidth: 200,
      },
      {
        Header: i18n.t('Type'),
        accessor: 'node.accountType',
        width: 130,
        Cell: ({ value }: *) => <TypeBadge type={value}>{value}</TypeBadge>,
      },
      {
        Header: i18n.t('Normal Balance'),
        accessor: 'node.normalBalance',
        width: 130,
      },
      {
        Header: i18n.t('Balance'),
        accessor: 'node.balance',
        width: 140,
        Cell: ({ value }: *) => (
          <BalanceCell value={value}>{formatCurrency(value)}</BalanceCell>
        ),
      },
      {
        Header: i18n.t('Status'),
        accessor: 'node.isActive',
        width: 100,
        Cell: ({ value }: *) => (
          <span>
            <StatusDot active={value} />
            {value ? i18n.t('Active') : i18n.t('Inactive')}
          </span>
        ),
      },
      {
        Header: i18n.t('System'),
        accessor: 'node.isSystem',
        width: 80,
        Cell: ({ value }: *) => (value ? i18n.t('Yes') : ''),
      },
    ];
  };

  render() {
    const { glAccountEdges, onEdit } = this.props;

    if (!glAccountEdges || glAccountEdges.length === 0) {
      return null;
    }

    return (
      <Wrapper>
        <Table
          data={glAccountEdges}
          columns={this.getColumns()}
          onRowClick={(rowData: Object) => onEdit(rowData.node)}
          contextMenuItems={[
            {
              label: i18n.t('Edit'),
              onClick: (row: Object) => onEdit(row.node),
            },
            {
              label: i18n.t('Delete'),
              onClick: (row: Object) => this.handleDelete(row.node),
            },
          ]}
        />
      </Wrapper>
    );
  }
}

export default createFragmentContainer(ChartOfAccountsTable, {
  glAccountEdges: graphql`
    fragment ChartOfAccountsTable_glAccountEdges on GLAccountEdge
      @relay(plural: true) {
      node {
        id
        accountNumber
        name
        description
        accountType
        normalBalance
        balance
        isActive
        isSystem
        parentAccount {
          id
          name
        }
      }
    }
  `,
});
